import React from 'react';
import { Permission } from '../components/Permission';

/**
 * Higher-Order Component (HOC) that wraps a component with a permission check.
 * If the user has the specified permission, renders the wrapped component.
 * Otherwise, renders the provided fallback (e.g., a NotFound or AccessDenied page).
 *
 * @param {string} permission - The required permission key for access.
 * @param {React.ReactNode} [fallback=null] - The element to render if permission is denied.
 * @returns {function(React.ComponentType): React.ComponentType} HOC that renders the wrapped component with a permission guard.
 *
 * @example
 * // Wrap a page with permission check and default fallback:
 * const ProtectedPage = withPermissionGuard(PERMISSIONS.VIEW_PAGE)(PageComponent);
 *
 * // Wrap with a custom fallback:
 * const ProtectedPage = withPermissionGuard(PERMISSIONS.EDIT_PAGE, <AccessDenied />)(PageComponent);
 *
 * // Use in a route:
 * <Route path="/protected" Component={ProtectedPage} />
 */
export function withPermissionGuard(permission, fallback = null) {
    return function (WrappedComponent) {
        const ComponentWithGuard = (props) => (
            <Permission permission={permission} fallback={fallback}>
                <WrappedComponent {...props} />
            </Permission>
        );

        ComponentWithGuard.displayName = `withPermissionGuard(${
            WrappedComponent.displayName || WrappedComponent.name || 'Component'
        })`;

        return ComponentWithGuard;
    };
}
